(() => {
  'use strict';
  const start = () => {
    let cart = null;
    let trigger = null;
    let scheduled = false;
    let shadowRoot = null;
    let shadowObserver = null;
    let rootPoll = null;
    const inertCarts = new WeakSet();
    const cartSelector = '#UpcartPopup #CartPopup.upcart-cart.styles_active__';
    const modalSelector = 'dialog[open], [role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]';
    const controlSelector = 'a[href], button, input, select, textarea, iframe, [tabindex], [contenteditable="true"]';
    const deepActiveElement = () => {
      let active = document.activeElement;
      while (active && active.shadowRoot && active.shadowRoot.activeElement) {
        active = active.shadowRoot.activeElement;
      }
      return active;
    };
    let lastOutsideFocus = deepActiveElement();
    const parentAcrossRoots = element => element.parentElement || element.getRootNode().host || null;
    const within = (root, element) => {
      while (element) {
        if (root === element || root.contains(element)) return true;
        element = element.getRootNode().host || null;
      }
      return false;
    };
    const visible = element => {
      if (!element || !element.isConnected || !element.getClientRects().length ||
          getComputedStyle(element).visibility === 'hidden') return false;
      for (let node = element; node; node = parentAcrossRoots(node)) {
        if (node.matches('[hidden], [inert], [aria-hidden="true"]')) return false;
      }
      return true;
    };
    const descendants = root => [...root.querySelectorAll('*')].flatMap(element =>
      element.shadowRoot ? [element, ...descendants(element.shadowRoot)] : [element]
    );
    const focusable = root => descendants(root).filter(element =>
      element.matches(controlSelector) && !element.disabled && element.tabIndex >= 0 && visible(element)
    );
    const roots = () => [document.getElementById('upCart')?.shadowRoot || shadowRoot, document].filter(Boolean);
    const activeCart = () => roots().map(root => root.querySelector(cartSelector)).find(Boolean) || null;
    const anotherDialogIsOpen = () => {
      const activeRoot = deepActiveElement()?.getRootNode();
      const searchRoots = new Set([...roots(), activeRoot].filter(Boolean));
      return [...searchRoots].some(root =>
        [...root.querySelectorAll(modalSelector)].some(element => element !== cart && visible(element))
      );
    };
    const ensureCloseControl = active => {
      const close = active?.querySelector('.upcart-public-header__close-button');
      if (close && !close.hasAttribute('tabindex')) close.setAttribute('tabindex', '0');
      return close;
    };
    const focusStart = () => {
      if (!cart || anotherDialogIsOpen()) return;
      const close = ensureCloseControl(cart);
      const target = close && visible(close) ? close : focusable(cart)[0] || cart;
      target.focus({preventScroll: true});
    };
    const discoverRoot = () => {
      const host = document.getElementById('upCart');
      const nextRoot = host?.shadowRoot || null;
      if (nextRoot !== shadowRoot) {
        if (shadowObserver) shadowObserver.disconnect();
        shadowRoot = nextRoot;
        shadowObserver = null;
        if (shadowRoot) {
          shadowObserver = new MutationObserver(schedule);
          shadowObserver.observe(shadowRoot, {
            subtree: true, childList: true, attributes: true, attributeFilter: ['class']
          });
        }
      }
      if (host && !shadowRoot && !host.querySelector('#CartPopup')) {
        if (!rootPoll) rootPoll = setTimeout(() => { rootPoll = null; schedule(); }, 250);
      } else if (rootPoll) {
        clearTimeout(rootPoll);
        rootPoll = null;
      }
    };
    const hideInactiveCarts = active => {
      const focused = deepActiveElement();
      roots().forEach(root => {
        root.querySelectorAll('#UpcartPopup #CartPopup.upcart-cart').forEach(popup => {
          if (popup === active) return;
          // Closed drawers can retain their layout box while moving offscreen.
          popup.setAttribute('aria-modal', 'false');
          // Move focus out before hiding its ancestor from assistive technology.
          if (!within(popup, focused)) {
            popup.setAttribute('aria-hidden', 'true');
            if (!popup.hasAttribute('inert')) {
              popup.setAttribute('inert', '');
              inertCarts.add(popup);
            }
          }
        });
      });
    };
    const sync = () => {
      scheduled = false;
      discoverRoot();
      const active = activeCart();
      if (active && inertCarts.has(active)) {
        active.removeAttribute('inert');
        inertCarts.delete(active);
      }
      if (active) ensureCloseControl(active);
      // Also handle a drawer that is already closed when the app mounts.
      hideInactiveCarts(active);
      if (active === cart) return;
      const previous = cart;
      if (active) {
        if (!previous) {
          const focused = deepActiveElement();
          const candidate = focused && focused !== document.body && !within(active, focused) ?
            focused : lastOutsideFocus;
          trigger = candidate && !within(active, candidate) ? candidate : null;
        }
        cart = active;
        cart.setAttribute('role', 'dialog');
        cart.setAttribute('aria-modal', 'true');
        cart.setAttribute('aria-hidden', 'false');
        if (!cart.hasAttribute('aria-label') && !cart.hasAttribute('aria-labelledby')) {
          cart.setAttribute('aria-label', window.theme.strings.cartTitle);
        }
        if (!cart.hasAttribute('tabindex')) cart.setAttribute('tabindex', '-1');
        focusStart();
      } else {
        cart = null;
        const focused = deepActiveElement();
        if (trigger && visible(trigger) && !anotherDialogIsOpen() &&
            (focused === document.body || within(previous, focused))) {
          trigger.focus({preventScroll: true});
        }
        trigger = null;
        if (document.getElementById('CartPageForm')) {
          document.dispatchEvent(new CustomEvent('cart:build', {
            detail: {source: 'zaft-upcart-close'}
          }));
        }
      }
      hideInactiveCarts(active);
    };
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(sync);
    }
    const isCartFocus = element => roots().some(root => {
      const popup = root.querySelector('#CartPopup');
      return popup && within(popup, element);
    });
    document.addEventListener('focusin', event => {
      const focused = event.composedPath()[0];
      if (!isCartFocus(focused)) lastOutsideFocus = focused;
      schedule();
    }, true);
    document.addEventListener('pointerdown', event => {
      const target = event.composedPath()[0];
      const control = target.closest?.('a[href], button, [role="button"], [tabindex]');
      if (control && !isCartFocus(control)) lastOutsideFocus = control;
    }, true);
    new MutationObserver(schedule).observe(document.body, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['class']
    });
    document.addEventListener('keydown', event => {
      if (!cart || anotherDialogIsOpen()) return;
      const current = deepActiveElement();
      const close = ensureCloseControl(cart);
      if ((event.key === 'Enter' || event.key === ' ') && current === close &&
          close.tagName !== 'BUTTON' && !event.defaultPrevented) {
        event.preventDefault();
        close.click();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = focusable(cart);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) {
        event.preventDefault();
        cart.focus({preventScroll: true});
      } else if (!within(cart, current) || current === cart) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({preventScroll: true});
      } else if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus({preventScroll: true});
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus({preventScroll: true});
      }
    });
    sync();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {once: true});
  } else {
    start();
  }
})();
